import React, { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import { Sun, Info } from "lucide-react";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";

import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateSunbedConfigs } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

import { useAuth } from "../../auth/AuthContext";

function isActiveFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "active";
}

const SunbedSettings = () => {
  const toast = useToast();
  const { user, updateUser, syncSettingsToLocalStorage } = useAuth();
  const { settingsData, refreshSettings } = useOutletContext<SettingsOutletContextType>();
  const [enableSunbed, setEnableSunbed] = useState(true);
  const [enableSunbedTmax, setEnableSunbedTmax] = useState(true);
  const [enableCoolingDuration, setEnableCoolingDuration] = useState(false);
  const [coolingDurationMinutes, setCoolingDurationMinutes] = useState("");
  const [enableWaitingList, setEnableWaitingList] = useState(false);
  const [enableMarketplaceBooking, setEnableMarketplaceBooking] = useState(true);
  const [kioskDelayMinutes, setKioskDelayMinutes] = useState("3");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (settingsData && settingsData.settings) {
      const s = settingsData.settings;
      setEnableSunbed(!!s.is_sunbed);
      setEnableSunbedTmax(!!s.is_sunbed_tmax);
      setEnableCoolingDuration(isActiveFlag(s.is_sunbed_cooling));
      setEnableWaitingList(isActiveFlag(s.is_sunbed_waiting_list));
      // Default ON when column missing (backward compatible)
      setEnableMarketplaceBooking(
        s.is_sunbed_marketplace_booking === undefined ||
          s.is_sunbed_marketplace_booking === null
          ? true
          : isActiveFlag(s.is_sunbed_marketplace_booking)
      );
      const mins = s.sunbed_cooling_duration;
      setCoolingDurationMinutes(
        mins != null && mins !== "" && !Number.isNaN(Number(mins)) ? String(mins) : ""
      );
      const kioskDelay = s.kiosk_delay_minutes;
      setKioskDelayMinutes(
        kioskDelay != null && kioskDelay !== "" && !Number.isNaN(Number(kioskDelay))
          ? String(kioskDelay)
          : "3"
      );
    }
  }, [settingsData]);

  const handleSave = async () => {
    if (enableCoolingDuration) {
      const mins = Number(coolingDurationMinutes);
      if (!coolingDurationMinutes.trim() || Number.isNaN(mins) || mins < 1) {
        toast.error("Enter a cooling duration of at least 1 minute.");
        return;
      }
    }

    const delayMins = Number(kioskDelayMinutes);
    if (
      !kioskDelayMinutes.trim() ||
      Number.isNaN(delayMins) ||
      delayMins < 0 ||
      !Number.isInteger(delayMins)
    ) {
      toast.error("Enter a valid kiosk delay of 0 or more minutes.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        is_sunbed: (enableSunbed ? "active" : "inactive") as "active" | "inactive",
        is_sunbed_tmax: (enableSunbedTmax ? "active" : "inactive") as "active" | "inactive",
        is_sunbed_cooling: (enableCoolingDuration ? "active" : "inactive") as
          | "active"
          | "inactive",
        is_sunbed_waiting_list: (enableWaitingList ? "active" : "inactive") as
          | "active"
          | "inactive",
        is_sunbed_marketplace_booking: (enableMarketplaceBooking ? "active" : "inactive") as
          | "active"
          | "inactive",
        kiosk_delay_minutes: delayMins,
        ...(enableCoolingDuration
          ? { sunbed_cooling_duration: Number(coolingDurationMinutes) }
          : {}),
      };

      await updateSunbedConfigs(payload);

      if (user && user.company) {
        updateUser({
          company: {
            ...user.company,
            is_sunbed: enableSunbed,
            is_sunbed_tmax: enableSunbedTmax,
            is_sunbed_cooling: enableCoolingDuration,
            is_sunbed_waiting_list: enableWaitingList,
            is_sunbed_marketplace_booking: enableMarketplaceBooking,
            kiosk_delay_minutes: delayMins,
            ...(enableCoolingDuration
              ? { sunbed_cooling_duration: Number(coolingDurationMinutes) }
              : {}),
          },
        });
      }

      toast.success("Sunbed settings updated successfully!");
      refreshSettings();
      await syncSettingsToLocalStorage();
    } catch (error: any) {
      console.error("Failed to update sunbed settings", error);
      toast.error("Failed to update sunbed settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading sunbed settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sunbed Settings"
        description="Configure sunbed visibility and integration options"
        variant="card"
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Sun size={18} className="text-white" />
            Sunbed Configuration
          </h3>
        </div>

        <div className="p-5 flex-1">
          <div className="space-y-5">
            <div>
              <SectionHeader title="Sunbed Settings" />

              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  Control sunbed visibility, marketplace online booking, TMAX, cooling, waiting
                  list, and kiosk delay
                </p>
              </div>

              <div className="space-y-4">
                <ToggleSetting
                  label="Sunbed Visibility"
                  description="Enable sunbed services for customers"
                  checked={enableSunbed}
                  onChange={setEnableSunbed}
                />

                <ToggleSetting
                  label="Sunbed TMAX Integration"
                  description="Enable sunbed integration with TMAX system"
                  checked={enableSunbedTmax}
                  onChange={setEnableSunbedTmax}
                />

                <ToggleSetting
                  label="Sunbed cooling duration"
                  description="Require a cooling period before the next sunbed session can start"
                  checked={enableCoolingDuration}
                  onChange={setEnableCoolingDuration}
                />

                {enableCoolingDuration && (
                  <div className="ml-1 pl-3 border-l-2 border-purple-100">
                    <label className="block text-xs lg:text-sm font-medium text-gray-900 mb-1.5">
                      Cooling duration (minutes)
                    </label>
                    <p className="text-[10px] lg:text-xs text-gray-500 mb-2">
                      Minimum wait time after a session ends before another can begin on the same
                      bed.
                    </p>
                    <Input
                      type="number"
                      min={1}
                      max={999}
                      step={1}
                      value={coolingDurationMinutes}
                      onChange={(e) => setCoolingDurationMinutes(e.target.value)}
                      placeholder="e.g. 5"
                      className="max-w-[200px] border-gray-200"
                    />
                  </div>
                )}

                <ToggleSetting
                  label="Sunbed waiting list"
                  description="Allow customers to join a queue when no sunbed rooms are available"
                  checked={enableWaitingList}
                  onChange={setEnableWaitingList}
                />

                <ToggleSetting
                  label="Marketplace online booking"
                  description="Allow customers to buy sunbed packages / book online from the Glossa marketplace"
                  checked={enableSunbed && enableMarketplaceBooking}
                  onChange={setEnableMarketplaceBooking}
                />

                <div className="p-2 lg:p-3 rounded-lg flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs lg:text-sm font-medium text-gray-900 truncate">
                      Kiosk delay (minutes)
                    </label>
                    <p className="text-[10px] lg:text-xs text-gray-500 mt-0.5 leading-relaxed">
                      Default delay before a kiosk-created sunbed session starts.
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={999}
                    step={1}
                    value={kioskDelayMinutes}
                    onChange={(e) => setKioskDelayMinutes(e.target.value)}
                    placeholder="3"
                    className="w-[100px] shrink-0 border-gray-200"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SunbedSettings;
